import { useRef, useState } from 'react';
import type { CSSProperties, JSX } from 'react';
import type { Palette } from '../../../../_theme/palette.js';
import { hexWithAlpha } from '../../../../_theme/palette.js';
import type { SurveyQuestion } from '../../types.js';
import { uploadAttachmentDataUrl, type UploadedAttachment } from '../../../../api.js';
import type { ShellHydration } from '../../../../api.js';

interface FileUploadFieldProps {
  palette: Palette;
  question: SurveyQuestion;
  value: unknown;
  onChange: (value: UploadedAttachment | null) => void;
  hydration: ShellHydration;
}

const MAX_FILE_SIZE_FALLBACK = 10 * 1024 * 1024;

// Drag-and-drop file upload. Tapping the zone on mobile opens the OS file
// picker (Camera / Photo Library / Browse on iOS). Drop events are
// intercepted on desktop so a respondent can drag an attachment in from
// outside the survey without leaving the page.
export function FileUploadField({ palette, question, value, onChange, hydration }: FileUploadFieldProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const uploaded = value as UploadedAttachment | null | undefined;
  const accept = question.settings?.allowedFileTypes?.trim() || undefined;
  const maxBytes = (question.settings?.maxFileSize ?? 0) > 0
    ? (question.settings?.maxFileSize ?? 0) * 1024 * 1024
    : MAX_FILE_SIZE_FALLBACK;

  const handleFile = async (file: File): Promise<void> => {
    if (file.size > maxBytes) {
      setError(`File too large. Max ${Math.round(maxBytes / 1024 / 1024)} MB.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const dataUrl = await readAsDataUrl(file);
      const result = await uploadAttachmentDataUrl(hydration, dataUrl, file.name, 'file');
      if (!result) {
        setError('Upload failed. Try again.');
      } else {
        onChange(result);
      }
    } catch (uploadError) {
      setError((uploadError as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (file) await handleFile(file);
  };

  const handleDrop = (event: React.DragEvent): void => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  const clear = (): void => {
    onChange(null);
    setError(null);
  };

  return (
    <div>
      {uploaded ? (
        <UploadedFileRow palette={palette} file={uploaded} onClear={clear} />
      ) : (
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload a file"
          onClick={() => !busy && inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDrop={handleDrop}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          style={dropZoneStyle(palette, busy, dragOver)}
        >
          <UploadIcon color={palette.textMuted} />
          <p style={dropTitleStyle(palette)}>
            {busy ? 'Uploading…' : 'Drag and drop or click to upload'}
          </p>
          {accept && <p style={dropHintStyle(palette)}>{accept}</p>}
        </div>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        hidden
        onChange={(event) => void handleChange(event)}
      />
      {error && <p style={errorStyle()}>{error}</p>}
    </div>
  );
}

function UploadedFileRow({
  palette,
  file,
  onClear,
}: {
  palette: Palette;
  file: UploadedAttachment;
  onClear: () => void;
}): JSX.Element {
  const rawSize = (file as unknown as { size?: number }).size;
  const sizeKb = typeof rawSize === 'number' ? `${(rawSize / 1024).toFixed(1)} KB` : null;
  return (
    <div style={uploadedRowStyle(palette)}>
      <UploadIcon color={palette.primary} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: palette.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {file.name ?? file.key}
        </p>
        {sizeKb && <p style={{ margin: 0, fontSize: 12, color: palette.textMuted }}>{sizeKb}</p>}
      </div>
      <button
        type="button"
        onClick={onClear}
        className="chirp-btn"
        style={removeButtonStyle(palette)}
        aria-label="Remove file"
      >
        Remove
      </button>
    </div>
  );
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(new Error('Could not read file'));
    reader.readAsDataURL(file);
  });
}

const PREMIUM_EASE = 'cubic-bezier(0.16, 1, 0.3, 1)';

function dropZoneStyle(palette: Palette, busy: boolean, dragOver: boolean): CSSProperties {
  return {
    width: '100%',
    padding: '24px 20px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    border: `2px dashed ${dragOver ? palette.primary : palette.border}`,
    background: dragOver
      ? hexWithAlpha(palette.primary, 0.06)
      : hexWithAlpha(palette.textMuted, 0.04),
    color: palette.textMuted,
    borderRadius: palette.radius,
    cursor: busy ? 'wait' : 'pointer',
    transition: `border-color 220ms ${PREMIUM_EASE}, background-color 220ms ${PREMIUM_EASE}`,
    textAlign: 'center',
  };
}

function dropTitleStyle(palette: Palette): CSSProperties {
  return { margin: 0, fontSize: 14, color: palette.textMuted };
}

function dropHintStyle(palette: Palette): CSSProperties {
  return { margin: 0, fontSize: 11, color: hexWithAlpha(palette.textMuted, 0.7) };
}

function uploadedRowStyle(palette: Palette): CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    border: `1px solid ${palette.border}`,
    borderRadius: palette.radius,
    background: palette.card,
  };
}

function removeButtonStyle(palette: Palette): CSSProperties {
  return {
    background: 'transparent',
    border: 'none',
    color: palette.textMuted,
    fontSize: 12,
    cursor: 'pointer',
    padding: '4px 8px',
  };
}

function errorStyle(): CSSProperties {
  return { margin: '8px 0 0', color: '#dc2626', fontSize: 12 };
}

function UploadIcon({ color }: { color: string }): JSX.Element {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}
